import { Entity } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import {
  InfoCard,
  Link,
  Progress,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusPending,
  StatusRunning,
  StatusWarning,
  WarningPanel,
} from '@backstage/core-components';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';

type DeploymentStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'skipped'
  | 'failed'
  | 'canceled'
  | 'unknown';

type Deployment = {
  source: string;
  sourceDeploymentId: string;
  environment: string;
  version: string;
  versionKind: 'tag' | 'commit' | 'other';
  status: DeploymentStatus;
  createdAt?: string;
  updatedAt?: string;
  steps: {
    sequence: number;
    action: string;
    status: Exclude<DeploymentStatus, 'unknown'>;
    error?: string;
    startedAt?: string;
    finishedAt?: string;
  }[];
  sourceUrl?: string;
  pipeline?: { id: number; iid: number; status: string; url: string };
};

type EnvironmentsResponse = {
  application?: {
    /** CAP applications. */
    id?: string;
    slug?: string;
    name: string;
    url?: string;
    /** ArgoCD applications. */
    namespace?: string;
    syncStatus?: string;
    healthStatus?: string;
  };
  environments: Deployment[];
};

/**
 * Which entities get the card. Two sources answer for it now: CAP for Cloudbear
 * internal applications, which is an explicit annotation, and ArgoCD for the
 * CBWS core services, which usually is not annotated because the source can
 * resolve an Application from the cluster's naming convention.
 *
 * So the CBWS side is a guess, and it is allowed to be one: an entity the
 * backend cannot place gets a 404 with `reason: not-deployed` and the card
 * removes itself, which is the same outcome as never rendering. The cost of
 * guessing wide is one request on the pages of entities that turn out not to be
 * deployed (protobufs, platform-docs, gitops).
 */
export const isDeploymentsAvailable = (entity: Entity): boolean =>
  Boolean(entity.metadata.annotations?.['platform.cloudbear.it/app-slug']) ||
  Boolean(entity.metadata.annotations?.['argoproj.io/app-name']) ||
  (entity.metadata.namespace === 'cbws' &&
    (entity.kind === 'Component' || entity.kind === 'System'));

/**
 * Why `unknown` gets no colour: most CAP deployments have no steps because they
 * came through the legacy commit path, and CAP's own rule reports an empty step
 * list as `pending`. Rendering that as an amber spinner would claim a deploy
 * from last week is still in progress, so it is shown as a plain fact instead.
 */
const StatusLabel = ({ deployment }: { deployment: Deployment }) => {
  const failure = deployment.steps.find(
    step => step.status === 'failed' || step.status === 'canceled',
  );
  const running = deployment.steps.find(step => step.status === 'running');

  switch (deployment.status) {
    case 'completed':
      // A completed v1.wait-for-sync is the only real "it is live and healthy"
      // signal CAP gives: ArgoCD reported the revision Synced and Healthy.
      return <StatusOK>completed</StatusOK>;
    case 'failed':
    case 'canceled':
      return (
        <>
          <StatusError>{deployment.status}</StatusError>
          {failure?.error && (
            <Typography variant="caption" component="div" color="textSecondary">
              {failure.error}
            </Typography>
          )}
        </>
      );
    case 'running':
      return (
        <>
          <StatusRunning>running</StatusRunning>
          {running && (
            <Typography variant="caption" component="div" color="textSecondary">
              {running.action}
            </Typography>
          )}
        </>
      );
    case 'pending':
      return <StatusPending>pending</StatusPending>;
    case 'unknown':
      return (
        <Tooltip title="CAP recorded this deployment but has no steps for it, so it cannot report whether it succeeded. Applications deploying through the legacy commit path report no progress.">
          <span>
            <StatusAborted>no status</StatusAborted>
          </span>
        </Tooltip>
      );
    default:
      return <StatusAborted>{deployment.status}</StatusAborted>;
  }
};

const VersionLabel = ({ deployment }: { deployment: Deployment }) => {
  const label =
    deployment.versionKind === 'commit'
      ? deployment.version.slice(0, 7)
      : deployment.version;
  return (
    <Typography variant="body2" component="span">
      {label}
    </Typography>
  );
};

const When = ({ at }: { at?: string }) => (
  <Typography variant="body2" color="textSecondary">
    {at
      ? new Date(at).toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'unknown'}
  </Typography>
);

/**
 * ArgoCD's own verdict on the application, above the deployment rows.
 *
 * Two independent facts, deliberately not merged. `sync` compares the cluster
 * with git: OutOfSync means someone changed one without the other. `health`
 * describes the running workloads, and it is the one that catches a service
 * whose last deploy succeeded and which is broken anyway, which is the state
 * `core-compute` is in with a worker stuck in CrashLoopBackOff.
 */
const ArgoState = ({
  application,
}: {
  application: NonNullable<EnvironmentsResponse['application']>;
}) => {
  const synced = application.syncStatus === 'Synced';
  const healthy = application.healthStatus === 'Healthy';

  return (
    <Box display="flex" alignItems="center" gridGap={16} pb={1}>
      {application.syncStatus && (
        <Typography variant="body2" component="span">
          {synced ? (
            <StatusOK>Synced</StatusOK>
          ) : (
            <StatusWarning>{application.syncStatus}</StatusWarning>
          )}
        </Typography>
      )}
      {application.healthStatus && (
        <Typography variant="body2" component="span">
          {healthy ? (
            <StatusOK>Healthy</StatusOK>
          ) : (
            <StatusWarning>{application.healthStatus}</StatusWarning>
          )}
        </Typography>
      )}
      <Typography variant="caption" color="textSecondary">
        {`ArgoCD application ${application.namespace}/${application.name}`}
      </Typography>
    </Box>
  );
};

type RuntimeState = {
  namespace: string;
  selector?: string;
  liveVersion?: string;
  versions?: string[];
  unhealthy: boolean;
  workloads: {
    name: string;
    ready: number;
    desired: number;
    version?: string;
    restarts: number;
    lastTerminatedReason?: string;
    crashloops: {
      pod: string;
      container: string;
      restarts: number;
      message?: string;
    }[];
  }[];
  waypoint?: {
    name: string;
    programmed: boolean;
    ready: number;
    desired: number;
  };
};

/**
 * What the cluster says right now, under the deployment rows.
 *
 * The deployment rows answer "what was deployed and did it work"; this answers
 * "and is it actually running", which is a different question with a different
 * answer more often than anyone would like. `compute` was Synced with its last
 * sync succeeding while its worker sat at 0 of 3 replicas for two months.
 *
 * Every workload is listed, in the order the cluster returns them, rather than
 * only the unhappy ones: knowing a service has six workloads and which five are
 * fine is part of reading the sixth. Order is left alone so the list does not
 * rearrange itself as things break, and the broken row is the only coloured one.
 */
const RuntimeSummary = ({ runtime }: { runtime: RuntimeState }) => {
  return (
    <Box pt={1}>
      <Box display="flex" alignItems="center" gridGap={16}>
        <Typography variant="body2" component="span">
          {runtime.unhealthy ? (
            <StatusWarning>Running</StatusWarning>
          ) : (
            <StatusOK>Running</StatusOK>
          )}
        </Typography>
        {runtime.liveVersion && (
          <Typography variant="body2">
            {`live version ${runtime.liveVersion}`}
          </Typography>
        )}
        {runtime.versions && (
          <Tooltip title="The workloads are not all on the same version">
            <Typography variant="body2">
              {`live versions ${runtime.versions.join(', ')}`}
            </Typography>
          </Tooltip>
        )}
        <Typography variant="caption" color="textSecondary">
          {`in ${runtime.namespace}`}
        </Typography>
      </Box>
      {runtime.workloads.map(workload => (
        <Box key={workload.name} pt={0.5} pl={2}>
          {/*
            One row, because the Status components render as blocks and would
            otherwise put the name, the replica count and the restarts on three
            separate lines.
          */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
            <Typography variant="body2" component="span" color="textSecondary">
              {`${workload.name}:`}
            </Typography>
            <Typography variant="body2" component="span">
              {workload.ready < workload.desired ? (
                <StatusError>{`${workload.ready}/${workload.desired} ready`}</StatusError>
              ) : (
                `${workload.ready}/${workload.desired} ready`
              )}
            </Typography>
            {workload.restarts > 0 && (
              <Typography variant="body2" component="span">
                {`${workload.restarts} restarts${
                  workload.lastTerminatedReason
                    ? ` (last exit: ${workload.lastTerminatedReason})`
                    : ''
                }`}
              </Typography>
            )}
            {/*
              Only when it disagrees with the header: repeating the same version
              on every row would bury the one row that is behind.
            */}
            {workload.version && workload.version !== runtime.liveVersion && (
              <Typography variant="body2" component="span">
                {workload.version}
              </Typography>
            )}
          </Box>
          {/*
            The container's own message, verbatim. This is the line that named
            the missing Temporal namespace; paraphrasing it would have lost the
            only clue on the page.
          */}
          {workload.crashloops[0]?.message && (
            <Typography variant="caption" color="textSecondary" component="div">
              {workload.crashloops[0].message}
            </Typography>
          )}
        </Box>
      ))}
      {/*
        The namespace's Istio waypoint, last and marked as what it is: mesh
        infrastructure that every service in the namespace shares, not one of
        this service's workloads.
      */}
      {runtime.waypoint && (
        <Box pt={0.5} pl={2} display="flex" alignItems="center" gridGap={8}>
          <Typography variant="body2" component="span" color="textSecondary">
            {`${runtime.waypoint.name} (mesh):`}
          </Typography>
          <Typography variant="body2" component="span">
            {runtime.waypoint.programmed ? (
              `${runtime.waypoint.ready}/${runtime.waypoint.desired} ready, programmed`
            ) : (
              <StatusWarning>not programmed</StatusWarning>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export const EntityDeploymentsCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const {
    kind,
    namespace = 'default',
    name,
  } = {
    kind: entity.kind,
    namespace: entity.metadata.namespace,
    name: entity.metadata.name,
  };

  const { value, loading, error } = useAsync(async (): Promise<
    { environments: EnvironmentsResponse; runtime?: RuntimeState } | undefined
  > => {
    const baseUrl = await discoveryApi.getBaseUrl('deployments');
    const entityPath = `${encodeURIComponent(namespace)}/${encodeURIComponent(
      kind,
    )}/${encodeURIComponent(name)}`;

    // Both at once: they answer different questions and neither should wait for
    // the other. Runtime state is best-effort, since an entity can be deployed
    // by a source that has no cluster behind it (every CAP application).
    const [environments, runtime] = await Promise.all([
      fetch(`${baseUrl}/entity/${entityPath}/environments`),
      fetch(`${baseUrl}/entity/${entityPath}/runtime`).catch(() => undefined),
    ]);

    if (environments.status === 404) {
      // Not deployed through a known source. Hide the card rather than showing
      // an error: plenty of entities are not deployed at all.
      return undefined;
    }
    if (!environments.ok) {
      const body = await environments.json().catch(() => undefined);
      throw new Error(
        body?.error ??
          `Request failed with ${environments.status} ${environments.statusText}`,
      );
    }

    return {
      environments: (await environments.json()) as EnvironmentsResponse,
      ...(runtime?.ok && {
        runtime: (await runtime.json()) as RuntimeState,
      }),
    };
  }, [kind, namespace, name]);

  if (!loading && !error && !value) {
    return null;
  }

  const application = value?.environments.application;
  // ArgoCD applications carry no url, because ArgoCD's UI is internal-only and
  // a link nobody outside the network can open is worse than no link.
  const action = application?.url ? (
    <Link to={application.url}>
      {application.slug ? 'View in CAP' : 'View in ArgoCD'}
    </Link>
  ) : undefined;

  let content;
  if (loading) {
    content = <Progress />;
  } else if (error) {
    content = (
      <WarningPanel
        title="Could not load deployments"
        message={error.message}
      />
    );
  } else if (!value?.environments.environments.length) {
    content = (
      <Typography variant="body2" color="textSecondary">
        This application is known to a deployment source but has no deployments
        yet.
      </Typography>
    );
  } else {
    content = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Environment</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>When</TableCell>
            <TableCell>Pipeline</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {value.environments.environments.map(deployment => (
            <TableRow key={deployment.sourceDeploymentId}>
              <TableCell>
                <Typography variant="body2">
                  {deployment.environment}
                </Typography>
              </TableCell>
              <TableCell>
                <VersionLabel deployment={deployment} />
              </TableCell>
              <TableCell>
                <StatusLabel deployment={deployment} />
              </TableCell>
              <TableCell>
                <When at={deployment.createdAt} />
              </TableCell>
              <TableCell>
                {deployment.pipeline ? (
                  <Link to={deployment.pipeline.url}>
                    #{deployment.pipeline.id}
                  </Link>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    none
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <InfoCard title="Deployments" action={action}>
      {application?.syncStatus || application?.healthStatus ? (
        <ArgoState application={application} />
      ) : null}
      {content}
      {value?.runtime && <RuntimeSummary runtime={value.runtime} />}
    </InfoCard>
  );
};
